import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Grid,
    Alert,
    CircularProgress,
} from '@mui/material';
import { useGetEmailConfigQuery, useUpdateEmailConfigMutation } from '@/app/features/findADeal/findADealApiSlice';
import useSnackbar from '@/hooks/use-snackbar';

const FindADealEmailConfigPage = () => {
    const [formData, setFormData] = useState({
        findADealEmail1: '',
        findADealEmail2: '',
        findADealEmail3: '',
    });

    const { data, isLoading, error, refetch } = useGetEmailConfigQuery();
    const [updateEmailConfig, { isLoading: updateLoading }] = useUpdateEmailConfigMutation();
    const { showSnackbar, snackbar } = useSnackbar();

    React.useEffect(() => {
        if (data?.data) {
            setFormData({
                findADealEmail1: data.data.findADealEmail1 || '',
                findADealEmail2: data.data.findADealEmail2 || '',
                findADealEmail3: data.data.findADealEmail3 || '',
            });
        }
    }, [data]);

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            const result = await updateEmailConfig(formData).unwrap();

            if (result.success) {
                showSnackbar({
                    message: 'Email configuration updated successfully',
                    type: 'success',
                });
                refetch();
            } else {
                showSnackbar({
                    message: result.message || 'Failed to update email configuration',
                    type: 'error',
                });
            }
        } catch (error) {
            showSnackbar({
                message: 'An error occurred while updating email configuration',
                type: 'error',
            });
        }
    };

    const validateEmail = (email) => {
        if (!email) return true; // Empty email is valid
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isValidForm = () => {
        return (
            validateEmail(formData.findADealEmail1) &&
            validateEmail(formData.findADealEmail2) &&
            validateEmail(formData.findADealEmail3) &&
            (formData.findADealEmail1 || formData.findADealEmail2 || formData.findADealEmail3)
        );
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 3 }}>
                Failed to load email configuration
            </Alert>
        );
    }

    return (
        <>
            {snackbar}
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Find A Deal Email Configuration
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Configure the email addresses that will receive notifications when users submit Find A Deal enquiries.
                    You can configure up to 3 email addresses. At least one email address is required.
                </Typography>

                <Card>
                    <CardContent>
                        <Box component="form" onSubmit={handleSubmit}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Email Address 1"
                                        type="email"
                                        value={formData.findADealEmail1}
                                        onChange={handleInputChange('findADealEmail1')}
                                        error={!validateEmail(formData.findADealEmail1)}
                                        helperText={!validateEmail(formData.findADealEmail1) ? 'Please enter a valid email address' : ''}
                                        placeholder="Enter email address"
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Email Address 2"
                                        type="email"
                                        value={formData.findADealEmail2}
                                        onChange={handleInputChange('findADealEmail2')}
                                        error={!validateEmail(formData.findADealEmail2)}
                                        helperText={!validateEmail(formData.findADealEmail2) ? 'Please enter a valid email address' : ''}
                                        placeholder="Enter email address (optional)"
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Email Address 3"
                                        type="email"
                                        value={formData.findADealEmail3}
                                        onChange={handleInputChange('findADealEmail3')}
                                        error={!validateEmail(formData.findADealEmail3)}
                                        helperText={!validateEmail(formData.findADealEmail3) ? 'Please enter a valid email address' : ''}
                                        placeholder="Enter email address (optional)"
                                    />
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={!isValidForm() || updateLoading}
                                    sx={{ minWidth: 120 }}
                                >
                                    {updateLoading ? <CircularProgress size={20} /> : 'Save Configuration'}
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={() => refetch()}
                                    disabled={updateLoading}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        How it works:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        • When a user submits a Find A Deal enquiry, an email notification will be sent to all configured email addresses
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        • The email will contain all the details provided by the user including vehicle information, contact details, and enquiry timestamp
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        • At least one email address must be configured for the system to work properly
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        • You can leave email addresses 2 and 3 empty if you only want notifications sent to one address
                    </Typography>
                </Box>
            </Box>
        </>
    );
};

export default FindADealEmailConfigPage;








